import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { CheckIcon, SearchIcon, TrendingUpIcon, TargetIcon, FileTextIcon, BarChartIcon, LinkIcon, ZapIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function SEOService() {
  const features = [{
    icon: SearchIcon,
    title: 'Keyword Research',
    description: 'Target the right keywords your customers are searching for'
  }, {
    icon: FileTextIcon,
    title: 'On-Page SEO',
    description: 'Optimize content, meta tags, and site structure'
  }, {
    icon: LinkIcon,
    title: 'Link Building',
    description: 'Build quality backlinks to boost authority'
  }, {
    icon: TargetIcon,
    title: 'Local SEO',
    description: 'Dominate local search results in your area'
  }, {
    icon: BarChartIcon,
    title: 'Analytics Setup',
    description: 'Track performance with Google Analytics & Search Console'
  }, {
    icon: TrendingUpIcon,
    title: 'Ongoing Optimization',
    description: 'Continuous improvements to maintain rankings'
  }, {
    icon: ZapIcon,
    title: 'Performance Optimisation',
    description: 'Speed improvements and technical enhancements'
  }];
  const benefits = ['Increase organic traffic to your website', 'Rank higher in Google search results', 'Reach customers actively searching for you', 'Build long-term sustainable growth', 'Better ROI than paid advertising', 'Establish authority in your industry'];
  const process = [{
    step: '01',
    title: 'Audit & Research',
    description: 'We analyse your current SEO performance, research competitors, and identify opportunities for improvement.'
  }, {
    step: '02',
    title: 'Strategy Development',
    description: 'We create a customised SEO strategy based on your business goals, target audience, and competitive landscape.'
  }, {
    step: '03',
    title: 'Implementation',
    description: 'We optimise your website content, technical elements, and build quality backlinks to improve rankings.'
  }, {
    step: '04',
    title: 'Monitor & Report',
    description: 'We track your rankings, traffic, and conversions, providing monthly reports on progress and insights.'
  }, {
    step: '05',
    title: 'Ongoing Optimisation',
    description: 'SEO is ongoing. We continuously refine your strategy based on performance data and algorithm updates.'
  }];
  return <>
      <Helmet data-id="element-2278">
        <title data-id="element-2279">SEO Services | Bright Pixxel Studio</title>
        <meta name="description" content="Get found on Google with SEO built for UK small businesses — keyword research, on-page optimisation and local search. Grow your traffic, book a free audit." data-id="element-2280" />
        <link rel="canonical" href="https://bright-pixxel.com/services/seo" data-id="element-2281" />
        {/* Open Graph Meta Tags */}
        <meta property="og:locale" content="en_GB" data-id="element-2282" />
        <meta property="og:type" content="website" data-id="element-2283" />
        <meta property="og:title" content="SEO Services | Bright Pixxel Studio" data-id="element-2284" />
        <meta property="og:description" content="Get found on Google with SEO built for UK small businesses — keyword research, on-page optimisation and local search. Grow your traffic, book a free audit." data-id="element-2285" />
        <meta property="og:url" content="https://bright-pixxel.com/services/seo" data-id="element-2286" />
        <meta property="og:site_name" content="Bright Pixxel Studio" data-id="element-2287" />
        <meta property="og:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-2288" />
        <meta property="og:image:width" content="1200" data-id="element-2289" />
        <meta property="og:image:height" content="630" data-id="element-2290" />
        <meta property="og:image:type" content="image/png" data-id="element-2291" />
        <meta property="og:image:alt" content="Bright Pixxel Studio - SEO Services" data-id="element-2292" />
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-2293" />
        <meta name="twitter:title" content="SEO Services | Bright Pixxel Studio" data-id="element-2294" />
        <meta name="twitter:description" content="Get found on Google with SEO built for UK small businesses — keyword research, on-page optimisation and local search. Grow your traffic, book a free audit." data-id="element-2295" />
        <meta name="twitter:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-2296" />
        <meta name="twitter:image:alt" content="Bright Pixxel Studio - SEO Services" data-id="element-2297" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2298">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2299">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2300">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5
          }} className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center" data-id="element-2301">
              <SearchIcon size={40} className="text-[#0DC3DC]" data-id="element-2302" />
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-5xl font-bold mb-6 text-white" data-id="element-2303">
              SEO Services for UK Businesses
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg md:text-xl text-white/90 mb-8" data-id="element-2304">
              Improve your search engine rankings with technical SEO, keyword
              optimisation, and content strategy that drives organic traffic to
              your website.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2305">
              <Link to="/pricing" className="bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-white px-8 py-3 rounded-lg font-medium transition-colors shadow-lg" data-id="element-2306">
                Get a Free SEO Audit
              </Link>
              <Link to="/portfolio" className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#0C1B26] px-8 py-3 rounded-lg font-medium transition-colors" data-id="element-2307">
                View Results
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Features Grid */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-2308">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2309">
          <div className="max-w-6xl mx-auto" data-id="element-2310">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-center mb-12" data-id="element-2311">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2312">
                Complete SEO Solutions
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300" data-id="element-2313">
                Everything you need to dominate search results
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2314">
              {features.map((feature, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-[#F9FAFB] dark:bg-[#212C34] p-6 rounded-xl hover:shadow-lg transition-shadow" data-id="element-2315">
                  <div className="w-12 h-12 bg-[#0DC3DC]/20 rounded-lg flex items-center justify-center mb-4" data-id="element-2316">
                    <feature.icon size={24} className="text-[#0DC3DC]" data-id="element-2317" />
                  </div>
                  <h3 className="text-xl font-bold mb-2" data-id="element-2318">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-2319">
                    {feature.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Benefits Section with Image */}
      <section className="py-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-2320">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2321">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-2322">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-2323">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2324">
                Why SEO Matters for Your Business
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-2325">
                SEO is one of the most cost-effective ways to attract customers
                who are actively looking for your products or services.
              </p>
              <div className="space-y-4" data-id="element-2326">
                {benefits.map((benefit, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} className="flex items-center gap-3" data-id="element-2327">
                    <div className="w-6 h-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center flex-shrink-0" data-id="element-2328">
                      <CheckIcon size={14} className="text-[#0DC3DC]" data-id="element-2329" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300" data-id="element-2330">
                      {benefit}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="rounded-xl overflow-hidden shadow-xl" data-id="element-2331">
              <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="SEO analytics dashboard showing search rankings and traffic growth - Bright Pixxel Studio professional SEO services" className="w-full h-auto" loading="lazy" decoding="async" data-id="element-2332" />
            </motion.div>
          </div>
        </div>
      </section>
      {/* Process Section */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-2333">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2334">
          <div className="max-w-4xl mx-auto" data-id="element-2335">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-2336">
              Our SEO Process
            </motion.h2>
            <div className="space-y-8" data-id="element-2337">
              {process.map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="flex gap-6" data-id="element-2338">
                  <div className="flex-shrink-0" data-id="element-2339">
                    <div className="w-16 h-16 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center" data-id="element-2340">
                      <span className="text-2xl font-bold text-[#0DC3DC]" data-id="element-2341">
                        {item.step}
                      </span>
                    </div>
                  </div>
                  <div className="flex-grow" data-id="element-2342">
                    <h3 className="text-xl font-bold mb-2" data-id="element-2343">{item.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300" data-id="element-2344">
                      {item.description}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Stats Section */}
      <section className="py-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-2345">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2346">
          <div className="max-w-5xl mx-auto" data-id="element-2347">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-2348">
              Real Results from Our SEO Services
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2349">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-center" data-id="element-2350">
                <div className="text-5xl font-bold text-[#0DC3DC] mb-2" data-id="element-2351">
                  +250%
                </div>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-2352">
                  Average traffic increase within 6 months
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="text-center" data-id="element-2353">
                <div className="text-5xl font-bold text-[#0DC3DC] mb-2" data-id="element-2354">
                  #1-3
                </div>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-2355">
                  Average ranking position for target keywords
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="text-center" data-id="element-2356">
                <div className="text-5xl font-bold text-[#0DC3DC] mb-2" data-id="element-2357">
                  +180%
                </div>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-2358">
                  Average increase in qualified leads
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2359">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2360">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2361">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6 text-white" data-id="element-2362">
              Ready to Boost Your Rankings?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-white/90 mb-8" data-id="element-2363">
              Get a free SEO audit and discover how we can help you rank higher
              in Google and attract more customers.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-2364">
              <a href="/contact" className="inline-block bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-[#0C1B26] px-8 py-4 rounded-full font-bold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 text-lg" data-id="element-2365">
                Get your free homepage design
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}